import { OnInit, OnDestroy } from '@angular/core';
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RdsModule } from '@rabobank/rds-angular';
import { BatchAction } from '../batch-table/batch-table.component';
import { BatchTableComponent } from '../batch-table/batch-table.component';
import { ConnectorConfigService } from '../../services/connector-config.service';
import { BatchService } from '../../services/batch.service';
import { Batch, ConnectorOption } from '../../models/batch.interface';
import { FormsModule } from '@angular/forms';
import { Router} from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { BatchStateService, BatchListState } from '../../services/batch-state.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-batch-list',
  standalone: true,
  imports: [CommonModule, RdsModule, BatchTableComponent, FormsModule],
  template: `
    <div class="batch-container">
      <div class="connector-selector">
        <rds-select [(ngModel)]="selectedConnector" (ngModelChange)="onConnectorChange($event)">
          <rds-option *ngFor="let option of connectorOptions" [value]="option.value">
            {{ option.viewValue }}
          </rds-option>
        </rds-select>
      </div>

      <app-batch-table
        [connector]="selectedConnector"
        [batches]="batches"
        [loading]="loading"
        (batchAction)="onBatchAction($event)"
      >
      </app-batch-table>
    </div>
  `,
  styles: [
    `
      .batch-container {
        padding: 0.5rem; /* Minimal padding for maximum table space */
        height: calc(100vh - 60px); /* Full height minus header */
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      .connector-selector {
        margin-bottom: 0.5rem; /* Minimal margin */
        flex-shrink: 0;
      }
    `,
  ],
})
export class BatchListComponent implements OnInit, OnDestroy {
  connectorOptions: ConnectorOption[] = [];
  selectedConnector: string = '';
  batches: Batch[] = [];
  loading = false;
  private routerSub: Subscription | null = null;
  private popStateHandler: ((e: PopStateEvent) => void) | null = null;
  private pageShowHandler: ((e: PageTransitionEvent) => void) | null = null;

  constructor(
    private configService: ConnectorConfigService,
    private batchService: BatchService,
    private router: Router,
    private batchStateService: BatchStateService,
  ) {
    this.connectorOptions = this.configService.getConnectorOptions();
  }

  public ngOnInit() {
    // Determine selected connector from current URL (so navigation back to a connector route selects it)
    if (this.connectorOptions.length > 0) {
      const url = this.router.url || '';
      const fromRoute = this.mapRouteToConnector(url);
      this.selectedConnector = fromRoute || this.connectorOptions[0].value;

      // If navigation carried state (we navigate back from Verify), persist
      // that state into BatchStateService so the table can restore paging.
      try {
        const navState = window?.history?.state ?? {};
        if (navState && typeof navState === 'object' && navState.pageIndex !== undefined) {
          this.batchStateService.saveState(navState as BatchListState);
        }
      } catch (e) {
        // ignore
      }

      this.loadBatches();

      // Listen for browser forward/back navigation to ensure the batch list
      // refreshes when the user navigates back from an auth page or similar.
      if (typeof window !== 'undefined' && window && window.addEventListener) {
        try {
          this.popStateHandler = (ev: PopStateEvent) => {
            const url = this.router.url || window.location.pathname || '';
            const c = this.mapRouteToConnector(url);
            if (c) {
              // Reset pagination to defaults when navigating via browser controls
              try {
                const current = this.batchStateService.getState();
                this.batchStateService.saveState({
                  ...current,
                  pageIndex: 0,
                  pageSize: 10,
                  searchText: '',
                  statusFilter: '',
                  documentTypeFilter: '',
                  uploadedByFilter: '',
                  createdFromDate: '',
                  createdToDate: '',
                  sortField: undefined,
                  sortOrder: null,
                  checkedBatches: [],
                  selectedBatch: undefined,
                });
              } catch (e) {
                /* ignore */
              }
              this.loadBatches();
            }
          };
          // pageshow fires on forward/back navigation too and indicates persisted pages
          this.pageShowHandler = (_ev: PageTransitionEvent) => {
            const url = this.router.url || window.location.pathname || '';
            const c = this.mapRouteToConnector(url);
            if (c) {
              try {
                const current = this.batchStateService.getState();
                this.batchStateService.saveState({
                  ...current,
                  pageIndex: 0,
                  pageSize: 10,
                  searchText: '',
                  statusFilter: '',
                  documentTypeFilter: '',
                  uploadedByFilter: '',
                  createdFromDate: '',
                  createdToDate: '',
                  sortField: undefined,
                  sortOrder: null,
                  checkedBatches: [],
                  selectedBatch: undefined,
                });
              } catch (e) {
                /* ignore */
              }
              this.loadBatches();
            }
          };
          window.addEventListener('popstate', this.popStateHandler);
          window.addEventListener('pageshow', this.pageShowHandler);
        } catch (e) {
          // ignore in non-browser envs
        }
      }

      // Listen for future navigation events (in case the router reuses this component)
      this.routerSub = this.router.events.subscribe((evt) => {
        if (evt instanceof NavigationEnd) {
          // If navigation carried a saved BatchListState (from Verify), persist it
          // and reload batches so the table can restore paging/state.
          try {
            const navState = window?.history?.state ?? {};
            if (navState && typeof navState === 'object' && navState.pageIndex !== undefined) {
              this.batchStateService.saveState(navState as BatchListState);
              this.loadBatches();
              const c = this.mapRouteToConnector(evt.urlAfterRedirects || evt.url);
              if (c && c !== this.selectedConnector) this.selectedConnector = c;
              return;
            }
          } catch (e) {
            // ignore and continue
          }

          const c = this.mapRouteToConnector(evt.urlAfterRedirects || evt.url);
          if (c && c !== this.selectedConnector) {
            this.selectedConnector = c;
            this.loadBatches();
          }
        }
      });
    }
  }

  public ngOnDestroy() {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
    try {
      if (this.popStateHandler) {
        window.removeEventListener('popstate', this.popStateHandler);
        this.popStateHandler = null;
      }
    } catch (e) {}
    try {
      if (this.pageShowHandler) {
        window.removeEventListener('pageshow', this.pageShowHandler);
        this.pageShowHandler = null;
      }
    } catch (e) {}
  }

  public onConnectorChange(connector: string) {
    // Clear any persisted list state (search/filters) when switching connectors
    try {
      this.batchStateService.clearState();
    } catch (e) {
      /* ignore */
    }

    // Navigate to the specific batch screen based on the selected connector
    switch (connector) {
      case 'HK_TRADE':
        this.router.navigate(['/batches/hk-trade']);
        break;
      case 'AG_DESK':
        this.router.navigate(['/batches/agency-desk']);
        break;
      case 'PFEU':
        this.router.navigate(['/batches/pf-eu']);
        break;
      case 'PFNA':
        this.router.navigate(['/batches/pf-na']);
        break;
      case 'GNM':
        this.router.navigate(['/batches/gnm']);
        break;
      case 'FEES_MGMT':
        this.router.navigate(['/batches/fees']);
        break;
      case 'HK_LC':
        this.router.navigate(['/batches/hk-lc']);
        break;
      case 'VCF':
        this.router.navigate(['/batches/vcf']);
        break;
      default:
        console.warn('Unknown connector type:', connector);
    }
  }

  private mapRouteToConnector(url: string): string | null {
    if (!url) return null;
    const u = url.toLowerCase();
    if (u.includes('/batches/hk-trade')) return 'HK_TRADE';
    if (u.includes('/batches/agency-desk')) return 'AG_DESK';
    if (u.includes('/batches/gnm')) return 'GNM';
    if (u.includes('/batches/fees')) return 'FEES_MGMT';
    if (u.includes('/batches/hk-lc')) return 'HK_LC';
    if (u.includes('/batches/pf-eu')) return 'PFEU';
    if (u.includes('/batches/pf-na')) return 'PFNA';
    if (u.includes('/batches/vcf')) return 'VCF';
    return null;
  }

  private loadBatches() {
    if (!this.selectedConnector) return;
    this.loading = true;
    this.batchService.getBatchesByConnector(this.selectedConnector).subscribe({
      next: (bs) => {
        this.batches = bs || [];
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load batches', err);
        this.loading = false;
      },
    });
  }

  public onBatchAction(action: BatchAction): void {
    console.log('Action:', action.action, 'Batch:', action.batch);
  }
}
